import { observer } from 'mobx-react';
import React from 'react';

import { AxiosError } from 'axios';
import { useFormikContext } from 'formik';
import { Office, VirtualOffice } from 'types/firm';
import { OfficeFormValues, VirtualOfficeFormValues } from 'types/firmInfo';

import { Modal, Spacer, makeStyles } from '@bequestinc/wui';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { useGlobalContext } from 'hooks';
import {
  useCreateOfficeMutation,
  useCreateVirtualOfficeMutation,
} from 'hooks/query-hooks/offices/useOfficeMutation';

import parseErrors from 'utils/errors';
import formatServiceForSubmit from 'utils/office';

const useStyles = makeStyles()({
  root: {
    width: '80%',
    maxWidth: 1000,
    margin: 'auto',
  },
  button: {
    marginTop: '30px',
    marginLeft: '50px',
  },
});

interface PropTypes {
  open: boolean;
  onClose: () => void;
  addToCache: (data: Office | VirtualOffice) => void;
  afterSubmitAction: () => void;
  firmAttorneys: Array<{ name: string; pk: number }>;
}

const ReactivateOfficeModal = ({
  open,
  onClose,
  addToCache,
  afterSubmitAction,
  firmAttorneys,
}: PropTypes) => {
  const { classes } = useStyles();
  const { services } = useGlobalContext();
  const { values } = useFormikContext<OfficeFormValues | VirtualOfficeFormValues>();
  const attorneyNames = values.attorneyInfos as string[];
  const mutationOptions = {
    onSuccess: (data: any) => {
      addToCache(data);
      afterSubmitAction();
    },
  };

  const isVirtualOffice = (
    formValues: OfficeFormValues | VirtualOfficeFormValues,
  ): formValues is VirtualOfficeFormValues => {
    return !('street' in formValues);
  };

  const officeMutation = useCreateOfficeMutation(mutationOptions);
  const virtualOfficeMutation = useCreateVirtualOfficeMutation(mutationOptions);
  const createOfficeMutation = isVirtualOffice(values) ? virtualOfficeMutation : officeMutation;

  return (
    <Modal title="Reactivate Office?" open={open} onClose={onClose} className={classes.root}>
      <Grid container>
        <Grid item xs={12}>
          <Typography>
            There is a previously deleted office with this{' '}
            {isVirtualOffice(values) ? (values.zipCode ? 'zip code' : 'state') : 'address'}
            , do you want to reactivate it?
            <br />
            {isVirtualOffice(values) ? (
              <b>{values.zipCode ? `${values.zipCode}` : values.state}</b>
            ) : (
              <b>
                {values.street}
                {values.street2 && <br />} {values.street2}
                <br />
                {values.city} {values.state}, {values.zipCode}
                <br />
                {values.county}
              </b>
            )}
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={onClose} className={classes.button}>
            No, keep as deleted
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              createOfficeMutation.mutate({
                ...formatServiceForSubmit(services, values),
                attorneyInfos: firmAttorneys
                  .filter(attorney => attorneyNames.includes(attorney.name))
                  .map(attorney => attorney.pk),
                reactivate: true,
              })
            }
            className={classes.button}
          >
            Yes, reactivate office
          </Button>
        </Grid>
      </Grid>
      <Spacer v={16} />
      {createOfficeMutation.isError && (
        <Typography style={{ whiteSpace: 'pre-line', fontWeight: 'bold', color: 'red' }}>
          {parseErrors((createOfficeMutation.error as AxiosError).response)}
        </Typography>
      )}
    </Modal>
  );
};

export default observer(ReactivateOfficeModal);
